import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  Spinner,
  Tooltip,
  EmptyState,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Textarea,
} from "../../components/UI";
import { supportAPI } from "../../services/api";
import TicketAttachments from "../../components/Support/TicketAttachments";
import InlineAttachments from "../../components/Support/InlineAttachments";
import toast from "react-hot-toast";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineTicket,
  HiOutlineTag,
  HiOutlineViewList,
  HiOutlineClock,
  HiOutlineLink,
  HiOutlineSearch,
  HiOutlineEye,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineMail,
  HiOutlineCollection,
  HiOutlineChatAlt2,
  HiOutlinePaperClip,
} from "react-icons/hi";
import { formatLabel } from '../../utils/formatLabel';

const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const priorityBadge = (p) =>
  ({ low: "default", medium: "info", high: "warning", urgent: "danger" }[p] || "default");

const subTabs = [
  { id: "tickets", label: "Tickets", icon: HiOutlineTicket },
  { id: "categories", label: "Categories", icon: HiOutlineTag },
  { id: "stages", label: "Workflow Stages", icon: HiOutlineViewList },
  { id: "sla", label: "SLA Time Limits", icon: HiOutlineClock },
];

const Support = () => {
  const [activeSubTab, setActiveSubTab] = useState("tickets");

  // Shared data: stages & categories used by multiple panels
  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ["support-stages"],
    queryFn: async () => (await supportAPI.getStages()).data?.data || [],
  });
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["support-categories"],
    queryFn: async () => (await supportAPI.getCategories()).data?.data || [],
  });

  const stages = stagesData || [];
  const categories = categoriesData || [];

  return (
    <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <HiOutlineTicket className="w-7 h-7 text-primary-500" />
          Support Tickets
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Raise and track support tickets — manage categories, workflow stages and SLA time limits
        </p>
      </div>

      {/* Sub-tab navigation */}
      <Card padding="p-2">
        <div className="flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-primary-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </Card>

      {activeSubTab === "tickets" && (
        <TicketsPanel stages={stages} categories={categories} />
      )}
      {activeSubTab === "categories" && (
        <CategoriesPanel
          categories={categories}
          stages={stages}
          isLoading={categoriesLoading}
        />
      )}
      {activeSubTab === "stages" && (
        <StagesPanel stages={stages} isLoading={stagesLoading} />
      )}
      {activeSubTab === "sla" && <SlaPanel />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Tickets Panel — list, create, detail (updates, stage change, link, merge)
// ═══════════════════════════════════════════════════════════════════════════
const TicketsPanel = ({ stages, categories }) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ["support-tickets", stageFilter, priorityFilter, search],
    queryFn: async () => {
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (search.trim()) params.search = search.trim();
      const response = await supportAPI.getTickets(params);
      return response.data?.data || [];
    },
  });
  const tickets = ticketsData || [];

  const invalidate = () => {
    queryClient.invalidateQueries(["support-tickets"]);
    queryClient.invalidateQueries(["support-ticket"]);
  };

  return (
    <>
      {/* Filters + Add */}
      <Card padding="p-3 lg:p-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
              Support Tickets
            </h3>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <HiOutlinePlus className="w-4 h-4" />
                Create Ticket
              </span>
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Input
                placeholder="Search ticket #, subject, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
              />
            </div>
            <Select
              options={[
                { value: "", label: "All Stages" },
                ...stages
                  .filter((s) => s.isActive)
                  .map((s) => ({ value: s._id, label: s.name })),
              ]}
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="min-w-[140px]"
            />
            <Select
              options={[
                { value: "", label: "All Priorities" },
                ...priorityOptions,
              ]}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="min-w-[130px]"
            />
          </div>
        </div>
      </Card>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <EmptyState
            icon={<HiOutlineTicket className="w-8 h-8 text-gray-400" />}
            title="No tickets found"
            description="Create your first support ticket to get started"
            action={
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <HiOutlinePlus className="w-4 h-4" />
                  Create Ticket
                </span>
              </Button>
            }
          />
        </Card>
      ) : (
        <Card padding="p-0">
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow hoverable={false}>
                  <TableCell header>Ticket #</TableCell>
                  <TableCell header>Subject</TableCell>
                  <TableCell header>Category</TableCell>
                  <TableCell header>Stage</TableCell>
                  <TableCell header>Priority</TableCell>
                  <TableCell header>Customer</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow key={t._id}>
                    <TableCell>
                      <span className="font-mono text-xs font-semibold text-primary-700 whitespace-nowrap">
                        {t.ticketNumber}
                      </span>
                      {t.mergedInto && (
                        <Badge variant="default" size="sm">
                          merged
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-gray-900 max-w-[220px] truncate">
                        {t.subject}
                      </div>
                    </TableCell>
                    <TableCell>
                      {t.category ? (
                        <Badge variant="primary" size="sm">
                          {t.category.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {t.stage && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${t.stage.color}20`,
                            color: t.stage.color,
                          }}
                        >
                          {t.stage.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={priorityBadge(t.priority)} size="sm">
                        {formatLabel(t.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-700 truncate max-w-[140px]">
                        {t.customerName || t.customerEmail || "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleDateString()
                          : "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Tooltip text="Open Ticket">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5"
                          icon={<HiOutlineEye className="w-4 h-4" />}
                          onClick={() => setSelectedTicketId(t._id)}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {tickets.map((t) => (
              <div
                key={t._id}
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedTicketId(t._id)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs font-semibold text-primary-700">
                    {t.ticketNumber}
                  </span>
                  <Badge variant={priorityBadge(t.priority)} size="sm">
                    {formatLabel(t.priority)}
                  </Badge>
                </div>
                <p className="font-medium text-gray-900 truncate">{t.subject}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {t.stage && (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${t.stage.color}20`,
                        color: t.stage.color,
                      }}
                    >
                      {t.stage.name}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {t.customerName || t.customerEmail || "-"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        stages={stages}
        categories={categories.filter((c) => c.isActive)}
        onSuccess={invalidate}
      />
      {selectedTicketId && (
        <TicketDetailModal
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(null)}
          stages={stages}
          onSuccess={invalidate}
        />
      )}
    </>
  );
};

// ─── Create Ticket Modal ────────────────────────────────────────────────────
// Fixed category choices shown in the dropdown; "Other" asks for a new name
const TICKET_CATEGORY_OPTIONS = ["Technical", "Billing", "Product", "Service"];
const CATEGORY_OTHER = "__other__";

const CreateTicketModal = ({ isOpen, onClose, stages, categories, onSuccess }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    subject: "",
    description: "",
    category: "",
    newCategory: "",
    priority: "medium",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
  });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setForm({
        subject: "",
        description: "",
        category: "",
        newCategory: "",
        priority: "medium",
        customerName: "",
        customerEmail: "",
        customerPhone: "",
      });
    }
  }, [isOpen]);

  const createMutation = useMutation({
    mutationFn: (data) =>
      data instanceof FormData
        ? supportAPI.createTicketWithFiles(data)
        : supportAPI.createTicket(data),
    onSuccess: (response) => {
      toast.success(
        `Ticket ${response.data?.data?.ticketNumber} created — admins notified by email`,
      );
      queryClient.invalidateQueries(["support-tickets"]);
      onClose();
      onSuccess?.();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || error.message || "Failed to create ticket"),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return toast.error("Subject is required");
    if (!form.description.trim()) return toast.error("Description is required");
    const phoneDigits = (form.customerPhone || "").replace(/\D/g, "");
    if (form.customerPhone && form.customerPhone !== "+" && (phoneDigits.length < 7 || phoneDigits.length > 15)) {
      return toast.error("Customer phone must be 7-15 digits");
    }

    const payload = { ...form };

    if (form.category === CATEGORY_OTHER) {
      // "Other" → require a new category name; backend creates it if missing
      if (!form.newCategory.trim()) {
        return toast.error("Please add a new category name");
      }
      payload.category = undefined;
      payload.categoryName = form.newCategory.trim();
    } else if (form.category.startsWith("name:")) {
      // Default category not found in the org's list — send by name
      payload.category = undefined;
      payload.categoryName = form.category.slice(5);
    } else {
      payload.category = form.category || undefined;
    }
    delete payload.newCategory;

    if (files.length > 0) {
      const tooBig = files.find((f) => f.size > 50 * 1024 * 1024);
      if (tooBig) return toast.error(`"${tooBig.name}" is larger than 50MB`);
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
      files.forEach((f) => formData.append("files", f));
      createMutation.mutate(formData);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Support Ticket" size="lg">
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <Input
          label={<span className="font-medium">Subject <span className="text-red-500">*</span></span>}
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="e.g., Payment deducted twice"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Describe the issue in detail..."
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none text-sm sm:text-base transition-colors"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            label="Category"
            options={[
              { value: "", label: "— Select Category —" },
              ...TICKET_CATEGORY_OPTIONS.map((name) => {
                // Prefer the org's real category id so the ticket links cleanly
                const match = categories.find(
                  (c) => c.name.toLowerCase() === name.toLowerCase(),
                );
                return {
                  value: match?._id || `name:${name}`,
                  label: name,
                };
              }),
              { value: CATEGORY_OTHER, label: "Other" },
            ]}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          {form.category === CATEGORY_OTHER && (
            <Input
              label={<span className="font-medium">Add New Category <span className="text-red-500">*</span></span>}
              value={form.newCategory}
              onChange={(e) => setForm({ ...form, newCategory: e.target.value })}
              placeholder="e.g., Integration Request"
            />
          )}
          <Select
            label="Priority"
            options={priorityOptions}
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            label={<span className="font-medium">Customer Name</span>}
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            placeholder="e.g., John Doe"
          />
          <Input
            label={<span className="font-medium">Customer Email</span>}
            type="email"
            value={form.customerEmail}
            onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
            placeholder="customer@email.com"
          />
          <Input
            label={<span className="font-medium">Customer Phone</span>}
            type="tel"
            inputMode="tel"
            maxLength={16}
            value={form.customerPhone}
            onChange={(e) => {
              // digits only, optional leading "+" (max 15 digits)
              const raw = e.target.value;
              const digits = raw.replace(/\D/g, "").slice(0, 15);
              setForm({ ...form, customerPhone: (raw.trimStart().startsWith("+") ? "+" : "") + digits });
            }}
            placeholder="+91..."
          />
        </div>
        {/* Optional file attachments at creation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Attach Files <span className="text-xs text-gray-400 font-normal">(optional — screenshots, documents, images)</span>
          </label>
          <input
            type="file"
            multiple
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg
                       file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-600
                       hover:file:bg-primary-100 cursor-pointer border-2 border-gray-200 rounded-lg px-3 py-1.5"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {files.map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                >
                  {f.name}
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    className="ml-0.5 text-gray-400 hover:text-red-500"
                    title="Remove"
                  >
                    <HiOutlineX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4 border-t">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={createMutation.isPending}
          >
            Create Ticket
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── Ticket Detail Modal — updates, stage change, link, merge ───────────────
const TicketDetailModal = ({ ticketId, onClose, stages, onSuccess }) => {
  const queryClient = useQueryClient();
  const [updateMessage, setUpdateMessage] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = React.useRef(null);
  const [newStageId, setNewStageId] = useState("");
  const [stageReason, setStageReason] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const { data: ticketData, isLoading } = useQuery({
    queryKey: ["support-ticket", ticketId],
    queryFn: async () => (await supportAPI.getTicket(ticketId)).data,
  });
  const ticket = ticketData?.data;

  const invalidate = () => {
    queryClient.invalidateQueries(["support-tickets"]);
    queryClient.invalidateQueries(["support-ticket"]);
    onSuccess?.();
  };

  const stageMutation = useMutation({
    mutationFn: ({ stageId, reason }) =>
      supportAPI.changeStage(ticketId, stageId, reason),
    onSuccess: () => {
      toast.success("Status updated — admins notified");
      setNewStageId("");
      setStageReason("");
      invalidate();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to change stage"),
  });

  const activityMutation = useMutation({
    mutationFn: (data) => {
      if (pendingFiles.length === 0) return supportAPI.addActivity(ticketId, data);
      const formData = new FormData();
      formData.append("message", data.message);
      formData.append("type", data.type);
      formData.append("visibleToCustomer", String(data.visibleToCustomer));
      pendingFiles.forEach((f) => formData.append("files", f));
      return supportAPI.addActivityWithFiles(ticketId, formData);
    },
    onSuccess: () => {
      toast.success("Update added — admins notified");
      setUpdateMessage("");
      setInternalNote(false);
      setPendingFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      invalidate();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to add update"),
  });

  const handleAddUpdate = () => {
    if (!updateMessage.trim()) return toast.error("Message is required");
    activityMutation.mutate({
      message: updateMessage.trim(),
      type: internalNote ? "comment" : "update",
      visibleToCustomer: !internalNote,
    });
  };

  const handlePickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const tooBig = files.find((f) => f.size > 50 * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than 50MB`);
      return;
    }
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const unlinkMutation = useMutation({
    mutationFn: (targetId) => supportAPI.unlinkTicket(ticketId, targetId),
    onSuccess: (res) => {
      toast.success(res.data?.message || "Link removed");
      invalidate();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to remove link"),
  });

  const unmergeMutation = useMutation({
    mutationFn: (duplicateId) => supportAPI.unmergeTicket(ticketId, duplicateId),
    onSuccess: (res) => {
      toast.success(res.data?.message || "Merge reverted");
      invalidate();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to un-merge"),
  });

  const handleUnlink = (t) => {
    if (window.confirm(`Remove the link with ticket ${t.ticketNumber}?`)) {
      unlinkMutation.mutate(t._id);
    }
  };

  const handleUnmerge = (t) => {
    if (
      window.confirm(
        `Un-merge ticket ${t.ticketNumber}? Its history will be restored as a standalone ticket.`,
      )
    ) {
      unmergeMutation.mutate(t._id);
    }
  };

  if (!ticket && !isLoading) return null;

  return (
    <Modal
      isOpen={!!ticketId}
      onClose={onClose}
      title={ticket ? `Ticket ${ticket.ticketNumber}` : "Ticket"}
      size="lg"
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      ) : ticket ? (
        <div className="space-y-4">
          {ticketData?.merged && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              This ticket was merged into another ticket. See the master ticket for the
              full history.
            </div>
          )}

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-gray-900">{ticket.subject}</p>
              <Badge variant={priorityBadge(ticket.priority)} size="sm">
                {formatLabel(ticket.priority)}
              </Badge>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {ticket.description}
            </p>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {ticket.category && (
                <span className="px-2 py-0.5 bg-gray-100 rounded">
                  {ticket.category.name}
                </span>
              )}
              {ticket.stage && (
                <span
                  className="px-2 py-0.5 rounded font-medium"
                  style={{
                    backgroundColor: `${ticket.stage.color}20`,
                    color: ticket.stage.color,
                  }}
                >
                  {ticket.stage.name}
                </span>
              )}
              {ticket.customerName && <span>👤 {ticket.customerName}</span>}
              {ticket.customerEmail && <span>✉️ {ticket.customerEmail}</span>}
              {ticket.customerPhone && <span>📞 {ticket.customerPhone}</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-xs">
              <div>
                <p className="text-gray-500">Response due</p>
                <p className="font-medium text-gray-800">
                  {ticket.responseDueAt
                    ? new Date(ticket.responseDueAt).toLocaleString()
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Resolution due</p>
                <p className="font-medium text-gray-800">
                  {ticket.resolutionDueAt
                    ? new Date(ticket.resolutionDueAt).toLocaleString()
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">First response</p>
                <p className="font-medium text-gray-800">
                  {ticket.firstResponseAt
                    ? new Date(ticket.firstResponseAt).toLocaleString()
                    : "Pending"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Resolved</p>
                <p className="font-medium text-gray-800">
                  {ticket.resolvedAt
                    ? new Date(ticket.resolvedAt).toLocaleString()
                    : "Not yet"}
                </p>
              </div>
            </div>
            {ticket.stageChangeReason && (
              <p className="text-xs text-gray-600 pt-1">
                <span className="font-medium">Last stage change reason:</span>{" "}
                {ticket.stageChangeReason}
              </p>
            )}
          </div>

          {/* Stage change — a reason is required */}
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1">
                <Select
                  label="Change Workflow Stage"
                  value={newStageId}
                  onChange={(e) => setNewStageId(e.target.value)}
                  options={[
                    { value: "", label: `Current: ${ticket.stage?.name || "—"}` },
                    ...stages
                      .filter((s) => s.isActive && s._id !== ticket.stage?._id)
                      .map((s) => ({ value: s._id, label: s.name })),
                  ]}
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                disabled={!newStageId || !stageReason.trim()}
                loading={stageMutation.isPending}
                onClick={() =>
                  stageMutation.mutate({
                    stageId: newStageId,
                    reason: stageReason.trim(),
                  })
                }
              >
                Apply Stage
              </Button>
            </div>
            <Textarea
              label={
                <span className="font-medium">
                  Reason for stage change <span className="text-red-500">*</span>
                </span>
              }
              value={stageReason}
              onChange={(e) => setStageReason(e.target.value)}
              placeholder="e.g., Work completed and verified with the customer"
              rows={2}
            />
          </div>

          {/* Link & Merge actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineLink className="w-4 h-4" />}
              onClick={() => setShowLinkModal(true)}
            >
              Link Related Ticket
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineCollection className="w-4 h-4" />}
              onClick={() => setShowMergeModal(true)}
            >
              Merge Duplicate
            </Button>
          </div>

          {(ticket.linkedTickets?.length > 0 || ticket.mergedFrom?.length > 0) && (
            <div className="text-xs text-gray-600 space-y-2">
              {ticket.linkedTickets?.length > 0 && (
                <div>
                  <span className="font-medium">Linked:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ticket.linkedTickets.map((t) => (
                      <span
                        key={t._id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full"
                      >
                        <span className="font-mono">{t.ticketNumber}</span>
                        <button
                          onClick={() => handleUnlink(t)}
                          disabled={unlinkMutation.isPending}
                          title="Delete from linked tickets"
                          className="p-0.5 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                          <HiOutlineX className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {ticket.mergedFrom?.length > 0 && (
                <div>
                  <span className="font-medium">Merged from:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ticket.mergedFrom.map((t) => (
                      <span
                        key={t._id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-full"
                      >
                        <span className="font-mono">{t.ticketNumber}</span>
                        <button
                          onClick={() => handleUnmerge(t)}
                          disabled={unmergeMutation.isPending}
                          title="Delete from merged tickets (un-merge)"
                          className="p-0.5 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                          <HiOutlineX className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Add update */}
          <div className="border-t pt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Add Update / Comment
            </label>
            <textarea
              rows={3}
              value={updateMessage}
              onChange={(e) => setUpdateMessage(e.target.value)}
              placeholder="Write an update about this ticket..."
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none resize-y"
            />
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pendingFiles.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                    {f.name}
                    <button type="button" onClick={() => removePendingFile(i)} className="text-gray-400 hover:text-red-500">
                      <HiOutlineX className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={internalNote}
                    onChange={(e) => setInternalNote(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600"
                  />
                  Internal note (hidden from customer view)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handlePickFiles(e.target.files)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
                  title="Attach files"
                >
                  <HiOutlinePaperClip className="w-4 h-4" />
                </button>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddUpdate}
                loading={activityMutation.isPending}
              >
                Add Update
              </Button>
            </div>
          </div>

          {/* Legacy ticket-level attachments (pre-migration files only —
              new uploads go on their own message, below) */}
          <TicketAttachments attachments={ticket.attachments || []} />

          {/* Activity history */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Activity History
            </h4>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {[...ticket.activities].reverse().map((a, i) => (
                <div
                  key={a._id || i}
                  className={`p-2.5 rounded-lg text-sm ${
                    a.visibleToCustomer
                      ? "bg-gray-50"
                      : "bg-amber-50 border border-amber-100"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-gray-500">
                      {a.createdByName || "System"} ·{" "}
                      {new Date(a.createdAt).toLocaleString()}
                    </span>
                    {!a.visibleToCustomer && (
                      <Badge variant="warning" size="sm">
                        Internal
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-700 mt-1 whitespace-pre-wrap">
                    {a.message}
                  </p>
                  <InlineAttachments attachments={a.attachments} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <LinkMergeModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        ticket={ticket}
        mode="link"
        onSuccess={invalidate}
      />
      <LinkMergeModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        ticket={ticket}
        mode="merge"
        onSuccess={invalidate}
      />
    </Modal>
  );
};

// ─── Link / Merge Modal — pick another ticket ───────────────────────────────
const LinkMergeModal = ({ isOpen, onClose, ticket, mode, onSuccess }) => {
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState("");
  const [search, setSearch] = useState("");

  const { data: ticketsData } = useQuery({
    queryKey: ["support-tickets", "all-for-link"],
    queryFn: async () => (await supportAPI.getTickets({ limit: 100 })).data?.data || [],
    enabled: isOpen,
  });

  const candidates = (ticketsData || []).filter(
    (t) =>
      String(t._id) !== String(ticket?._id) &&
      (mode === "merge" ? !t.mergedInto : true) &&
      (!search.trim() ||
        t.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
        t.subject?.toLowerCase().includes(search.toLowerCase())),
  );

  const mutation = useMutation({
    mutationFn: (id) =>
      mode === "merge"
        ? supportAPI.mergeTicket(ticket._id, id)
        : supportAPI.linkTickets(ticket._id, id),
    onSuccess: (response) => {
      toast.success(
        response.data?.message ||
          (mode === "merge" ? "Tickets merged — admins notified" : "Tickets linked — admins notified"),
      );
      queryClient.invalidateQueries(["support-tickets"]);
      queryClient.invalidateQueries(["support-ticket"]);
      setTargetId("");
      onClose();
      onSuccess?.();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || error.message || "Action failed"),
  });

  useEffect(() => {
    if (isOpen) {
      setTargetId("");
      setSearch("");
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "merge" ? "Merge Duplicate Ticket" : "Link Related Ticket"}
      size="md"
    >
      <div className="space-y-3">
        {ticket && (
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            {mode === "merge" ? "Merge into" : "Link from"}{" "}
            <span className="font-semibold text-gray-900">
              {ticket.ticketNumber}
            </span>{" "}
            — {ticket.subject}
          </div>
        )}
        {mode === "merge" && (
          <p className="text-xs text-gray-500">
            The duplicate ticket's full history is carried over into this ticket, and
            the duplicate is marked as merged (its ticket number still redirects
            customers here).
          </p>
        )}
        <Input
          placeholder="Search by ticket number or subject..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
        />
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No other tickets found.
            </p>
          ) : (
            candidates.map((t) => (
              <button
                key={t._id}
                type="button"
                onClick={() => setTargetId(t._id)}
                className={`w-full px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  targetId === t._id
                    ? "bg-primary-50 text-primary-700 border-2 border-primary-300"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span className="font-mono text-xs font-semibold">
                  {t.ticketNumber}
                </span>
                <span className="ml-2 truncate">{t.subject}</span>
                {t.mergedInto && (
                  <Badge variant="default" size="sm">
                    merged
                  </Badge>
                )}
              </button>
            ))
          )}
        </div>
        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!targetId}
            loading={mutation.isPending}
            onClick={() => mutation.mutate(targetId)}
          >
            {mode === "merge" ? "Merge" : "Link"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Categories Panel — all tickets with a category filter
// ═══════════════════════════════════════════════════════════════════════════
const CategoriesPanel = ({ categories, stages, isLoading }) => {
  const queryClient = useQueryClient();

  // Category filter + tickets list
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const ticketsQuery = useQuery({
    queryKey: ["support-tickets", "by-category", categoryFilter],
    queryFn: async () =>
      (
        await supportAPI.getTickets({
          category: categoryFilter || undefined,
          limit: 100,
        })
      ).data?.data || [],
  });
  const tickets = ticketsQuery.data || [];

  const invalidate = () => {
    queryClient.invalidateQueries(["support-tickets"]);
  };

  return (
    <>
      {/* All tickets with category filter */}
      <Card padding="p-3 lg:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
              All Tickets
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Filter the tickets by their category
            </p>
          </div>
          <div className="sm:w-64">
            <Select
              options={[
                { value: "", label: "All Categories" },
                ...categories
                  .filter((c) => c.isActive)
                  .map((c) => ({ value: c._id, label: c.name })),
              ]}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card padding="p-0">
        {ticketsQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={<HiOutlineTicket className="w-8 h-8 text-gray-400" />}
            title="No tickets found"
            description={
              categoryFilter
                ? "No tickets in this category yet"
                : "Tickets will appear here once created"
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <Table>
              <TableHead>
                <TableRow hoverable={false}>
                  <TableCell header>Ticket</TableCell>
                  <TableCell header>Subject</TableCell>
                  <TableCell header>Category</TableCell>
                  <TableCell header>Priority</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow
                    key={t._id}
                    className="cursor-pointer"
                    onClick={() => setSelectedTicketId(t._id)}
                  >
                    <TableCell>
                      <span className="font-mono text-xs font-semibold text-primary-700">
                        {t.ticketNumber}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-900 text-sm">{t.subject}</span>
                    </TableCell>
                    <TableCell>
                      {t.category ? (
                        <Badge variant="info" size="sm">
                          {t.category.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={priorityBadge(t.priority)} size="sm">
                        {t.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {t.stage && (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${t.stage.color}20`,
                            color: t.stage.color,
                          }}
                        >
                          {t.stage.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-500">
                        {new Date(t.createdAt).toLocaleDateString()}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {tickets.map((t) => (
                <div
                  key={t._id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedTicketId(t._id)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-xs font-semibold text-primary-700">
                      {t.ticketNumber}
                    </span>
                    <Badge variant={priorityBadge(t.priority)} size="sm">
                      {t.priority}
                    </Badge>
                  </div>
                  <p className="font-medium text-gray-900 truncate">{t.subject}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {t.category && (
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                        {t.category.name}
                      </span>
                    )}
                    {t.stage && (
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${t.stage.color}20`,
                          color: t.stage.color,
                        }}
                      >
                        {t.stage.name}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Ticket detail */}
      {selectedTicketId && (
        <TicketDetailModal
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(null)}
          stages={stages}
          onSuccess={invalidate}
        />
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Workflow Stages Panel — all tickets with their workflow data. Per-ticket
// actions: change stage (Pending, In Progress, Work Done, Rejected, Closed...),
// activate/deactivate, edit, view (full work history).
// ═══════════════════════════════════════════════════════════════════════════
const StagesPanel = ({ stages }) => {
  const queryClient = useQueryClient();
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketSearch, setTicketSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Pending stage change — a reason is required before it is applied
  const [pendingStage, setPendingStage] = useState(null);
  const [stageReason, setStageReason] = useState("");

  // All tickets with their data — actions taken directly from the table
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ["support-stage-tickets", ticketSearch, stageFilter, statusFilter],
    queryFn: async () => {
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      if (statusFilter) params.active = statusFilter;
      if (ticketSearch.trim()) params.search = ticketSearch.trim();
      const response = await supportAPI.getTickets(params);
      return response.data?.data || [];
    },
  });
  const tickets = ticketsData || [];
  const activeStages = stages.filter((s) => s.isActive);

  const refreshAll = () => {
    queryClient.invalidateQueries(["support-stage-tickets"]);
    queryClient.invalidateQueries(["support-tickets"]);
    queryClient.invalidateQueries(["support-ticket"]);
  };

  const changeStageMutation = useMutation({
    mutationFn: ({ id, stageId, reason }) =>
      supportAPI.changeStage(id, stageId, reason),
    onSuccess: (res) => {
      toast.success(res.data?.message || "Ticket stage updated");
      setPendingStage(null);
      setStageReason("");
      refreshAll();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to change stage"),
  });

  const closeStageModal = () => {
    setPendingStage(null);
    setStageReason("");
  };

  const handleStageReasonSubmit = () => {
    if (!stageReason.trim())
      return toast.error("Reason for stage change is required");
    changeStageMutation.mutate({
      id: pendingStage.ticketId,
      stageId: pendingStage.stageId,
      reason: stageReason.trim(),
    });
  };

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }) =>
      supportAPI.toggleTicketActive(id, isActive),
    onSuccess: (res) => {
      toast.success(res.data?.message || "Ticket status updated");
      refreshAll();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to update ticket"),
  });

  const stageOptionsFor = (t) => {
    const opts = activeStages.map((s) => ({ value: s._id, label: s.name }));
    // Keep the current stage selectable in case it was deactivated
    if (t.stage && !opts.some((o) => o.value === t.stage._id)) {
      opts.unshift({ value: t.stage._id, label: `${t.stage.name} (inactive)` });
    }
    return opts;
  };

  const stageSelect = (t, extraClass = "") => (
    <select
      value={t.stage?._id || ""}
      onChange={(e) => {
        const stageId = e.target.value;
        if (stageId && stageId !== t.stage?._id) {
          setStageReason("");
          setPendingStage({
            ticketId: t._id,
            ticketNumber: t.ticketNumber,
            fromStage: t.stage,
            toStage: stages.find((s) => s._id === stageId),
            stageId,
          });
        }
      }}
      className={`text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 ${extraClass}`}
    >
      {stageOptionsFor(t).map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <Card padding="p-3 lg:p-4">
        <div>
          <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
            Workflow Stages
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            All tickets with their data and workflow status. Use the Actions
            column on each ticket to move it to Pending, Work In Progress,
            Work Done, Rejected, Closed or any other stage, deactivate/activate
            it, or open it to view the full work history.
          </p>
        </div>
      </Card>

      {/* Filters */}
      <Card padding="p-3 lg:p-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <Input
              placeholder="Search ticket #, subject, customer..."
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
              icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
            />
          </div>
          <Select
            options={[
              { value: "", label: "All Stages" },
              ...activeStages.map((s) => ({ value: s._id, label: s.name })),
            ]}
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="min-w-[140px]"
          />
          <Select
            options={[
              { value: "", label: "All Statuses" },
              { value: "true", label: "Active Only" },
              { value: "false", label: "Deactivated Only" },
            ]}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="min-w-[140px]"
          />
        </div>
      </Card>

      {ticketsLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <EmptyState
            icon={<HiOutlineTicket className="w-8 h-8 text-gray-400" />}
            title="No tickets found"
            description="Tickets will appear here with their data and workflow actions"
          />
        </Card>
      ) : (
        <Card padding="p-0">
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow hoverable={false}>
                  <TableCell header>Ticket #</TableCell>
                  <TableCell header>Subject</TableCell>
                  <TableCell header>Category</TableCell>
                  <TableCell header>Priority</TableCell>
                  <TableCell header>Customer</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Current Stage</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow key={t._id}>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs font-semibold text-primary-700 whitespace-nowrap">
                          {t.ticketNumber}
                        </span>
                        {t.isActive === false && (
                          <Badge variant="danger" size="sm">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-gray-900 max-w-[200px] truncate">
                        {t.subject}
                      </div>
                    </TableCell>
                    <TableCell>
                      {t.category ? (
                        <Badge variant="primary" size="sm">
                          {t.category.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={priorityBadge(t.priority)} size="sm">
                        {t.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-700 truncate max-w-[130px]">
                        {t.customerName || t.customerEmail || "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleDateString()
                          : "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {t.stage && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
                          style={{
                            backgroundColor: `${t.stage.color}20`,
                            color: t.stage.color,
                          }}
                        >
                          {t.stage.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          {stageSelect(t, "max-w-[130px]")}
                          <Tooltip text={t.isActive === false ? "Activate" : "Deactivate"}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              icon={
                                t.isActive === false ? (
                                  <HiOutlineCheck className="w-4 h-4 text-green-500" />
                                ) : (
                                  <HiOutlineX className="w-4 h-4 text-red-500" />
                                )
                              }
                              onClick={() =>
                                toggleActiveMutation.mutate({
                                  id: t._id,
                                  isActive: t.isActive === false,
                                })
                              }
                            />
                          </Tooltip>
                          <Tooltip text="View ticket & work history">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              icon={<HiOutlineEye className="w-4 h-4" />}
                              onClick={() => setSelectedTicketId(t._id)}
                            />
                          </Tooltip>
                        </div>
                        {t.stageChangeReason && (
                          <p
                            className="text-[10px] leading-tight text-gray-500 max-w-[180px] truncate"
                            title={`Stage change reason: ${t.stageChangeReason}`}
                          >
                            <span className="font-medium">Reason:</span>{" "}
                            {t.stageChangeReason}
                          </p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {tickets.map((t) => (
              <div
                key={t._id}
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedTicketId(t._id)}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono text-xs font-semibold text-primary-700">
                    {t.ticketNumber}
                  </span>
                  <div className="flex items-center gap-1">
                    {t.isActive === false && (
                      <Badge variant="danger" size="sm">
                        Inactive
                      </Badge>
                    )}
                    <Badge variant={priorityBadge(t.priority)} size="sm">
                      {t.priority}
                    </Badge>
                  </div>
                </div>
                <p className="font-medium text-gray-900 truncate">{t.subject}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {t.stage && (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${t.stage.color}20`,
                        color: t.stage.color,
                      }}
                    >
                      {t.stage.name}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 truncate">
                    {t.customerName || t.customerEmail || "-"}
                  </span>
                </div>
                <div
                  className="flex items-center gap-1 mt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {stageSelect(t, "flex-1")}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5"
                    icon={
                      t.isActive === false ? (
                        <HiOutlineCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <HiOutlineX className="w-4 h-4 text-red-500" />
                      )
                    }
                    onClick={() =>
                      toggleActiveMutation.mutate({
                        id: t._id,
                        isActive: t.isActive === false,
                      })
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5"
                    icon={<HiOutlineEye className="w-4 h-4" />}
                    onClick={() => setSelectedTicketId(t._id)}
                  />
                </div>
                {t.stageChangeReason && (
                  <p
                    className="text-[10px] text-gray-500 mt-1.5 truncate"
                    title={`Stage change reason: ${t.stageChangeReason}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="font-medium">Reason:</span>{" "}
                    {t.stageChangeReason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {selectedTicketId && (
        <TicketDetailModal
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(null)}
          stages={stages}
          onSuccess={refreshAll}
        />
      )}

      {/* Reason for stage change — required before a stage change is applied */}
      <Modal
        isOpen={!!pendingStage}
        onClose={closeStageModal}
        title={`Change Stage — ${pendingStage?.ticketNumber || "Ticket"}`}
        size="sm"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {pendingStage?.fromStage ? (
              <span
                className="px-2 py-0.5 rounded text-xs font-medium"
                style={{
                  backgroundColor: `${pendingStage.fromStage.color}20`,
                  color: pendingStage.fromStage.color,
                }}
              >
                {pendingStage.fromStage.name}
              </span>
            ) : (
              <span className="text-xs text-gray-500">No stage</span>
            )}
            <span className="text-gray-400">→</span>
            {pendingStage?.toStage && (
              <span
                className="px-2 py-0.5 rounded text-xs font-medium"
                style={{
                  backgroundColor: `${pendingStage.toStage.color}20`,
                  color: pendingStage.toStage.color,
                }}
              >
                {pendingStage.toStage.name}
              </span>
            )}
          </div>
          <Textarea
            label={
              <span className="font-medium">
                Reason for stage change <span className="text-red-500">*</span>
              </span>
            }
            value={stageReason}
            onChange={(e) => setStageReason(e.target.value)}
            placeholder="e.g., Work completed and verified with the customer"
            rows={3}
          />
          <div className="flex gap-2 justify-end pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={closeStageModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={changeStageMutation.isPending}
              onClick={handleStageReasonSubmit}
            >
              Change Stage
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SLA Panel — response & resolution time limits per priority
// ═══════════════════════════════════════════════════════════════════════════
const SlaPanel = () => {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState(null);

  const { data } = useQuery({
    queryKey: ["support-sla"],
    queryFn: async () => (await supportAPI.getSLA()).data?.data || [],
  });

  useEffect(() => {
    if (data) {
      setRows(
        data.map((d) => ({
          priority: d.priority,
          responseHours: d.responseHours,
          resolutionHours: d.resolutionHours,
        })),
      );
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (policies) => supportAPI.updateSLA(policies),
    onSuccess: () => {
      toast.success("SLA time limits saved");
      queryClient.invalidateQueries(["support-sla"]);
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Failed to save SLA"),
  });

  if (!rows) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const setRow = (priority, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.priority === priority ? { ...r, [field]: value } : r)),
    );
  };

  return (
    <>
      <Card padding="p-3 lg:p-4">
        <div>
          <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
            Response & Resolution Time Limits
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Define how quickly tickets must get a first response and be resolved,
            per priority. New tickets get deadlines from these limits.
          </p>
        </div>
      </Card>

      <Card padding="p-0">
        <Table>
          <TableHead>
            <TableRow hoverable={false}>
              <TableCell header>Priority</TableCell>
              <TableCell header>Response Time (hours)</TableCell>
              <TableCell header>Resolution Time (hours)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.priority}>
                <TableCell>
                  <Badge variant={priorityBadge(r.priority)} size="sm">
                    {r.priority}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={r.responseHours}
                    onChange={(e) =>
                      setRow(r.priority, "responseHours", e.target.value)
                    }
                    className="w-28"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={r.resolutionHours}
                    onChange={(e) =>
                      setRow(r.priority, "resolutionHours", e.target.value)
                    }
                    className="w-28"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex justify-end p-3 border-t">
          <Button
            variant="primary"
            size="sm"
            loading={updateMutation.isPending}
            onClick={() =>
              updateMutation.mutate(
                rows.map((r) => ({
                  ...r,
                  responseHours: Number(r.responseHours),
                  resolutionHours: Number(r.resolutionHours),
                })),
              )
            }
          >
            Save Time Limits
          </Button>
        </div>
      </Card>
    </>
  );
};

export default Support;